import { Box, Stack, Typography } from '@mui/material';
import { MutableRefObject, useRef } from 'react';
import {
  AutocompleteInput,
  AutocompleteInputProps,
  ReferenceInput,
  useTranslate,
} from 'react-admin';

import { GetB2bEmployeesOneResponse, GetB2bEmployeesQuery } from '@Common/types/bo/b2b/employees';

import { inputDisabledProps } from '@sh4res/bo-common/utils';
import { highlightMatch } from '@sh4res/bo-common/utils/highlight_match';

type B2bEmployeeSelectInputProps = Omit<
  AutocompleteInputProps<GetB2bEmployeesOneResponse>,
  'filter'
> & {
  source?: string;
  reference?: 'b2b/employees' | 'employees';
  searchBy?: keyof NonNullable<GetB2bEmployeesQuery['filter']>;
  filter?: GetB2bEmployeesQuery['filter'];
  inputText?: (record: GetB2bEmployeesOneResponse | null) => string;
  optionTextHeader?: (record: GetB2bEmployeesOneResponse | null) => string;
  detailsColumns?: (keyof GetB2bEmployeesOneResponse)[] | null;
  disabled?: boolean;
  testId?: string;
};

const inputTextDefault = (option: GetB2bEmployeesOneResponse | null) => {
  if (!option) {
    return '';
  }
  return `(${option.id}) ${option.lastname} ${option.firstname}`;
};

const optionTextDefault = (option: GetB2bEmployeesOneResponse | null) => {
  if (!option) {
    return '';
  }
  return `${option.lastname} ${option.firstname}`;
};

const EmployeeOption = ({
  record,
  queryRef: { current: query },
  detailsColumns,
  optionTextHeader,
}: {
  record: GetB2bEmployeesOneResponse;
  queryRef: MutableRefObject<string>;
  optionTextHeader: (record: GetB2bEmployeesOneResponse) => string;
  detailsColumns:
    | (
        | keyof GetB2bEmployeesOneResponse
        | { field: keyof GetB2bEmployeesOneResponse; label: string; flex: number }
      )[]
    | null;
}) => {
  const translate = useTranslate();
  return (
    <Stack spacing={0.5} sx={{ width: '100%', padding: 0 }}>
      <Typography variant="body1" sx={{ fontSize: '15px' }}>
        {highlightMatch(optionTextHeader(record), query)}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          fontSize: '13px',
        }}>
        {detailsColumns &&
          detailsColumns.map((column) => {
            const key = typeof column === 'object' ? column.field : column;
            const label =
              typeof column === 'object'
                ? column.label
                : `resources.bo_common.b2b.employees.fields.${key}`;
            const flex = typeof column === 'object' ? column.flex : 3;

            return (
              <Box key={key} sx={{ minWidth: '45px', flex }}>
                <Typography component="span" sx={{ color: '#909090', margin: 0, mr: 1 }}>
                  {translate(label)}
                </Typography>
                {highlightMatch(record[key]?.toString() ?? '-', query)}
              </Box>
            );
          })}
      </Box>
    </Stack>
  );
};

export const B2bEmployeeSelectInput = ({
  source = 'employee_id',
  reference = 'b2b/employees',
  searchBy = 'query',
  filter,
  isRequired,
  validate,
  inputText = inputTextDefault,
  optionTextHeader = optionTextDefault,
  detailsColumns = ['id', 'nir', 'registration_number'],
  disabled,
  testId,
  sx,
  ...props
}: B2bEmployeeSelectInputProps) => {
  const inputRef = useRef('');

  return (
    <ReferenceInput
      source={source}
      reference={reference}
      isRequired={isRequired}
      filter={filter}
      data-testid={testId}
      enableGetChoices={disabled ? () => false : undefined}>
      <AutocompleteInput
        // oxlint-disable-next-line react/no-unstable-nested-components -- render callback, not a component
        optionText={(record) => (
          <EmployeeOption
            record={record}
            queryRef={inputRef}
            detailsColumns={detailsColumns}
            optionTextHeader={optionTextHeader}
          />
        )}
        filterToQuery={(value: string) => {
          inputRef.current = value;
          return { [searchBy]: value };
        }}
        inputText={inputText}
        matchSuggestion={() => true}
        isRequired={isRequired}
        validate={validate}
        {...props}
        {...inputDisabledProps(disabled ?? false, sx)}
        data-testid={testId}
      />
    </ReferenceInput>
  );
};
